/**
 * Working-hypothesis SSOT — form → test → revise across Live + Discovery turns.
 *
 * Reuses existing sticky fields (sessionFacts.problem, dossier.pain/workflow,
 * role/industry inference). Do not add a dossier field — that would confuse
 * the PDF completeness gate.
 */
import {
  ADAPTIVE_PACE_RULE,
  CONSULTANT_VOICE_RULE,
  FIRST_TURN_HYPOTHESIS_OPENING,
  GENERIC_LIVE_IMPROV_MARKER,
  GREETING_HEDGE_MARKER,
  HYPOTHESIS_CORRECTION_BIND,
  HYPOTHESIS_LOOP_RULE,
  RESEARCH_TRICKLE_RULE,
} from '../../contracts.js'
import { hasEmergencySiteSignal } from '../../intelligence/grounded-research-claims.js'
import { humanizeFirmName } from '../../../lib/visitor-brief.js'

export type WorkingHypothesisSource =
  | 'visitor'
  | 'dossier_pain'
  | 'dossier_workflow'
  | 'inferred'

export type WorkingHypothesisStatus = 'confirmed' | 'guess'

export interface WorkingHypothesis {
  text: string
  status: WorkingHypothesisStatus
  source: WorkingHypothesisSource
}

export interface ResolveWorkingHypothesisInput {
  role?: string | null
  industry?: string | null
  companyName?: string | null
  dossierPain?: string | null
  dossierWorkflow?: string | null
  sessionProblem?: string | null
  /** True when company/person/facts/dossier/citations exist after welcome. */
  hasResearchContext?: boolean
  /** Visitor UI locale. Inferred guesses follow this. Visitor/dossier text is kept verbatim. */
  locale?: string | null
  /** Company summary / site pain. Emergency coverage beats the industry canned line. */
  siteSignal?: string | null
}

function normalizeHypothesisText(value: string | null | undefined): string {
  return value?.replace(/\s+/g, ' ').trim().slice(0, 220) || ''
}

export function consultingLineLocale(locale?: string | null): 'en' | 'nb' {
  const value = (locale || '').toLowerCase()
  if (value === 'nb' || value === 'no' || value.startsWith('nb-') || value.startsWith('nn')) return 'nb'
  return 'en'
}

const INFERRED_BOTTLENECK = {
  en: {
    marketing: 'turning demand into a consistent follow-up workflow',
    software: 'turning product momentum into a repeatable go-to-market workflow',
    services: 'turning expertise into a repeatable pipeline',
    local: 'turning incoming work into a reliable scheduling and follow-up flow',
    emergency: 'covering emergency callouts without dropping the scheduled jobs',
    fallback: 'turning the current growth priority into a repeatable workflow',
  },
  nb: {
    marketing: 'å gjøre etterspørsel om til en jevn oppfølgingsflyt',
    software: 'å gjøre produktmomentum om til en gjentakbar go-to-market-flyt',
    services: 'å gjøre fagkunnskap om til en gjentakbar pipeline',
    local: 'å gjøre innkommende jobb om til pålitelig planlegging og oppfølging',
    emergency: 'å dekke akuttvakt og utrykning uten at planlagt jobb stopper',
    fallback: 'å gjøre vekstprioriteten om til en gjentakbar arbeidsflyt',
  },
} as const

/**
 * Role/industry opening guess. Grounded only in those labels — no invented facts.
 * Locale picks the whole phrase so a Norwegian greeting does not embed an English clause.
 */
export function inferBottleneckHypothesis(
  role: string | null | undefined,
  industry: string | null | undefined,
  companyName?: string | null,
  locale?: string | null,
  siteSignal?: string | null,
): string {
  const copy = INFERRED_BOTTLENECK[consultingLineLocale(locale)]
  if (hasEmergencySiteSignal(siteSignal)) return copy.emergency
  const context = `${role || ''} ${industry || ''} ${companyName || ''}`.toLowerCase()
  if (/marketing|\bagency\b|growth|\bsales\b/.test(context)) return copy.marketing
  // "production" contains "product". Word boundaries keep media off the software guess.
  if (/\bmedia\b|entertainment|broadcast|\bfilm\b|television|\bproduction\b|\bproducer\b/.test(context))
    return copy.fallback
  if (/\bsoftware\b|\bsaas\b|\btechnology\b|\bai\b|\bproduct\b/.test(context)) return copy.software
  if (/professional services|consulting|advisory/.test(context)) return copy.services
  if (/construction|plumbing|plumb|electric|clinic|restaurant|hospitality|rørlegg/.test(context))
    return copy.local
  return copy.fallback
}

const NB_INDUSTRY_LABELS: Array<[RegExp, string]> = [
  [/plumb|rørlegg/, 'rørlegger'],
  [/construct|bygg|entrepren/, 'bygg'],
  [/electric|elektro/, 'elektro'],
  [/restaurant|hospitality|servering/, 'servering'],
  [/clinic|health|helse/, 'helse'],
  [/marketing|agency|growth|markedsfør/, 'markedsføring'],
  [/software|saas|\bai\b|technology|programvare/, 'programvare'],
  [/consult|advisory|professional services|rådgiv/, 'faglige tjenester'],
]

/** Known industry labels follow the greeting locale. Unknown labels stay verbatim. */
export function localizeIndustryLabel(
  industry: string | null | undefined,
  locale?: string | null,
): string {
  const raw = industry?.replace(/\s+/g, ' ').trim() || ''
  const norwegian = consultingLineLocale(locale) === 'nb'
  if (!raw) return norwegian ? 'denne bransjen' : 'this business'
  if (!norwegian) return raw
  const key = raw.toLowerCase()
  for (const [pattern, label] of NB_INDUSTRY_LABELS) {
    if (pattern.test(key)) return label
  }
  return raw
}

/** True when a dossier clause can sit inside the greeting template without mixing languages. */
export function clauseMatchesConsultingLocale(
  text: string | null | undefined,
  locale?: string | null,
): boolean {
  const value = text?.replace(/\s+/g, ' ').trim() || ''
  if (!value) return true
  if (consultingLineLocale(locale) !== 'nb') return true
  if (/[æøå]/i.test(value)) return true
  return /\b(og|ikke|er|det|som|med|for|en|et|den|denne|fra|til|av)\b/i.test(value)
}

const INSTRUCTION_LEAK_CUTS: RegExp[] = [
  /HYPOTHESIS CORRECTION/i,
  /NAMED PROBLEM/i,
  /do not read this instruction/i,
  /Adopted bottleneck\s*:/i,
  /They already named the problem and where the work sits/i,
  /Do not ask whether they use a CRM/i,
  /Do not ask a survey question/i,
  /One concrete action on Monday in that same place/i,
  /offer the written brief/i,
  /leads\s*\/\s*paperwork\s*\/\s*something else/i,
  /WORKING HYPOTHESIS \(/i,
]

const HYPOTHESIS_INSTRUCTION_LEAK = new RegExp(
  INSTRUCTION_LEAK_CUTS.map(pattern => pattern.source).join('|'),
  'i',
)

/**
 * Drop model-only binds. They are appended to the typed turn so the model
 * sees them, and Live / the canonical projection echo that text as a YOU row.
 * Cut at the earliest marker, including a paraphrased recitation of the bind.
 */
export function stripHypothesisInstructionLeak(text: string): string {
  if (!text || !HYPOTHESIS_INSTRUCTION_LEAK.test(text)) return text
  const cuts = INSTRUCTION_LEAK_CUTS.map(pattern => text.search(pattern)).filter(index => index >= 0)
  const cut = cuts.length > 0 ? Math.min(...cuts) : -1
  const head = cut >= 0 ? text.slice(0, cut) : text
  const kept = head
    .split(/(?<=[.!?\n])\s+/)
    .map(part => part.trim())
    .filter(part => part && !HYPOTHESIS_INSTRUCTION_LEAK.test(part))
  return kept.join(' ').replace(/\s+/g, ' ').trim()
}

/**
 * Priority: visitor-stated problem → dossier pain → dossier workflow → inferred guess.
 * Returns null when there is no research/context to defend a guess.
 */
export function resolveWorkingHypothesis(
  input: ResolveWorkingHypothesisInput
): WorkingHypothesis | null {
  const sessionProblem = normalizeHypothesisText(input.sessionProblem)
  if (sessionProblem) {
    return { text: sessionProblem, status: 'confirmed', source: 'visitor' }
  }

  const dossierPain = normalizeHypothesisText(input.dossierPain)
  if (dossierPain) {
    return { text: dossierPain, status: 'confirmed', source: 'dossier_pain' }
  }

  const dossierWorkflow = normalizeHypothesisText(input.dossierWorkflow)
  if (dossierWorkflow) {
    return { text: dossierWorkflow, status: 'guess', source: 'dossier_workflow' }
  }

  const hasRoleOrIndustry = Boolean(
    normalizeHypothesisText(input.role) ||
      normalizeHypothesisText(input.industry) ||
      normalizeHypothesisText(input.companyName)
  )
  if (!input.hasResearchContext && !hasRoleOrIndustry) return null

  return {
    text: inferBottleneckHypothesis(
      input.role,
      input.industry,
      input.companyName,
      input.locale,
      input.siteSignal,
    ),
    status: 'guess',
    source: 'inferred',
  }
}

export function formatWorkingHypothesisSeed(hypothesis: WorkingHypothesis): string {
  const label =
    hypothesis.status === 'confirmed'
      ? `WORKING HYPOTHESIS (confirmed — ${hypothesis.source === 'visitor' ? 'visitor' : 'dossier'}):`
      : 'WORKING HYPOTHESIS (guess):'
  return (
    `${label} ${hypothesis.text}\n` +
    'Replace this on visitor correction. Deepen this one — do not start a new survey category. ' +
    'Never ask leads / paperwork / something else. Do not rename their problem.\n' +
    RESEARCH_TRICKLE_RULE +
    '\n' +
    ADAPTIVE_PACE_RULE +
    '\n' +
    CONSULTANT_VOICE_RULE
  )
}

const UNKNOWN_FIRM =
  /^(your company|this business|your business|this company|denne bedriften|bedriften din)$/i

const UNKNOWN_VISITOR_NAME =
  /^(there|der|visitor|guest|friend|unknown|user|name|your name|navn)$/i

/** Registry-style firm for the spoken frame. Empty and generic labels stay unnamed. */
function spokenFirm(companyName: string | null | undefined): string | null {
  const raw = companyName?.replace(/\s+/g, ' ').trim() || ''
  if (!raw || UNKNOWN_FIRM.test(raw)) return null
  return humanizeFirmName(raw) || null
}

/** Spoken visitor name. Placeholders and email local-parts stay unnamed. */
function spokenVisitorName(name: string | null | undefined): string | null {
  const raw = name?.replace(/\s+/g, ' ').trim() || ''
  if (!raw || UNKNOWN_VISITOR_NAME.test(raw)) return null
  if (raw.includes('@') || /^https?:/i.test(raw)) return null
  if (!raw.includes(' ') && raw.includes('.')) return null
  return raw
}

const OPEN_FRAME = {
  en: {
    hi: (name: string | null) => (name ? `Hi ${name}.` : 'Hi.'),
    homework: (firm: string) =>
      `I've already looked into ${firm} and the space you operate in — enough to start.`,
    ask: 'What are you looking to solve, improve, or explore?',
  },
  nb: {
    hi: (name: string | null) => (name ? `Hei ${name}.` : 'Hei.'),
    homework: (firm: string) =>
      `Jeg har allerede sett på ${firm} og området dere jobber i — nok til å starte.`,
    ask: 'Hva vil du løse, forbedre eller utforske?',
  },
} as const

/**
 * Variant A first contact. Same string for the client mouth and Live.
 * Name when known, the firm once, then one question. No firm: the question only.
 * Chapter facts stay on the card. siteSignal and industry are not spoken.
 */
export function formatResearchAckOpening(input: {
  name?: string | null | undefined
  companyName?: string | null | undefined
  industry?: string | null | undefined
  locale?: string | null | undefined
  /** Pack / homepage text. Feeds the card via publicChapter, not this line. */
  siteSignal?: string | null | undefined
  /**
   * Public pages behind the card. Claim research only when this is greater than
   * zero. Omitted is not a claim: a firm name alone is not research.
   */
  sourceCount?: number | null | undefined
}): string {
  const frame = consultingLineLocale(input.locale) === 'nb' ? OPEN_FRAME.nb : OPEN_FRAME.en
  const name = spokenVisitorName(input.name)
  const firm = spokenFirm(input.companyName)
  const hi = frame.hi(name)
  const counted = typeof input.sourceCount === 'number' && input.sourceCount > 0
  if (!firm || !counted) return `${hi} ${frame.ask}`
  return `${hi} ${frame.homework(firm)} ${frame.ask}`
}

/**
 * Client mouth and Live seed must match formatResearchAckOpening.
 * The hypothesis is not spoken.
 */
export function formatFirstConsultingLine(input: {
  name?: string | null
  companyName?: string | null
  hypothesis?: WorkingHypothesis | null
  industry?: string | null
  locale?: string | null
  sourceCount?: number | null
}): string {
  return formatResearchAckOpening({
    name: input.name,
    companyName: input.companyName,
    industry: input.industry,
    locale: input.locale,
    ...(typeof input.sourceCount === 'number' ? { sourceCount: input.sourceCount } : {}),
  })
}

/**
 * First-turn opening for Live seed. Silent seed preamble says wait — this
 * overrides that for the first REAL spoken turn when a hypothesis exists.
 * The verbatim block is the same string the client speakAgentOutput uses.
 */
export function formatFirstTurnOpeningDirective(
  _hypothesis: WorkingHypothesis | null,
  visitor?: {
    name?: string | null
    companyName?: string | null
    industry?: string | null
    locale?: string | null
    sourceCount?: number | null
  },
): string {
  const line = formatFirstConsultingLine({
    name: visitor?.name ?? null,
    companyName: visitor?.companyName ?? null,
    industry: visitor?.industry ?? null,
    ...(visitor?.locale ? { locale: visitor.locale } : {}),
    ...(typeof visitor?.sourceCount === 'number' ? { sourceCount: visitor.sourceCount } : {}),
  })
  return (
    `${FIRST_TURN_HYPOTHESIS_OPENING}\n` +
    `FIRST CONSULTING LINE (verbatim):\n"""\n${line}\n"""\n` +
    `On visitor correction: REPLACE the working hypothesis with their words and deepen that one. ` +
    `Never ask a multi-choice survey (leads / paperwork / something else). ` +
    `Never say "${GREETING_HEDGE_MARKER}". Never say "${GENERIC_LIVE_IMPROV_MARKER}".\n` +
    RESEARCH_TRICKLE_RULE +
    '\n' +
    ADAPTIVE_PACE_RULE +
    '\n' +
    CONSULTANT_VOICE_RULE
  )
}

/** Visitor replaced the working guess — persist + bind, do not survey. */
export function isHypothesisCorrection(text: string | null | undefined): boolean {
  const value = text?.replace(/\s+/g, ' ').trim() || ''
  if (!value || /^\s*\[SAY\]/i.test(value) || /HYPOTHESIS CORRECTION/i.test(value)) {
    return false
  }
  if (/correct me if it's wrong/i.test(value) && /bottleneck guess/i.test(value)) {
    return false
  }
  if (/rett meg om det er feil/i.test(value) && /flaskehalsanslag/i.test(value)) {
    return false
  }
  if (/i'd start here|jeg ville startet her/i.test(value)) {
    return false
  }
  return (
    /quote[- ]to[- ]cash|\bq2c\b/i.test(value) ||
    /not (?:the )?(?:scheduling|follow-up|follow up)/i.test(value) ||
    /real bottleneck|actual bottleneck is|bottleneck is\b/i.test(value) ||
    /that'?s not (?:it|right|the bottleneck)|you'?re wrong/i.test(value) ||
    /not (?:leads|paperwork|scheduling)\b/i.test(value) ||
    /ekte flaskehals|flaskehalsen er|ikke planlegging|ikke oppfølging|tilbud[- ]til[- ]betaling/i.test(
      value,
    )
  )
}

export function formatHypothesisCorrectionBind(visitorText: string): string {
  const adopted = visitorText.replace(/\s+/g, ' ').trim().slice(0, 220)
  return (
    `${HYPOTHESIS_CORRECTION_BIND}\n` +
    `Adopted bottleneck: ${adopted}`
  )
}

export function getHypothesisLoopInstruction(): string {
  return HYPOTHESIS_LOOP_RULE
}
